/**
 * DEX Conditional Bot — regime-switching orchestrator for DEX automation.
 *
 * Mirrors the CEX Conditional Strategy concept but adapted for DEX:
 *   - References BTC/ETH/SOL for regime classification (via shared OHLCV)
 *   - Orchestrates user's existing DEX bots (Grid Bot, TV Webhooks, DCA)
 *   - Worker activates one inner bot per regime, pauses the others
 *   - Hysteresis: regime must hold for 3 ticks before a switch fires
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  GitBranch, Activity, AlertTriangle, CheckCircle, X, RefreshCw, PlayCircle,
  PauseCircle, Trash2, Plus, ChevronDown, ChevronUp, TrendingUp, TrendingDown,
  Minus, Sparkles, Target, Clock, Brain, Edit2, Save, ZoomIn,
} from 'lucide-react';
import { Spinner, InfoTip } from '../components/ui';
import {
  createDexConditionalBot, listDexConditionalBots, getDexConditionalBot,
  updateDexConditionalBot, deleteDexConditionalBot,
  activateDexConditionalBot, deactivateDexConditionalBot,
  simulateDexConditionalBot, listDexConditionalBotInnerBots,
} from '../services/api';

const REFERENCE_ASSETS = [
  { id: 'btc',  label: 'BTC',  desc: 'Bitcoin — macro / market-wide regime' },
  { id: 'eth',  label: 'ETH',  desc: 'Ethereum — smart-contract platforms regime' },
  { id: 'sol',  label: 'SOL',  desc: 'Solana — alt-L1 / risk-on regime' },
  { id: 'avax', label: 'AVAX', desc: 'Avalanche — alt-L1, more cyclical than SOL' },
  { id: 'link', label: 'LINK', desc: 'Chainlink — DeFi blue-chip / oracle ecosystem' },
  { id: 'arb',  label: 'ARB',  desc: 'Arbitrum — L2 ecosystem regime' },
  { id: 'op',   label: 'OP',   desc: 'Optimism — second L2, sometimes diverges from ARB' },
  { id: 'bnb',  label: 'BNB',  desc: 'BNB — exchange-token / centralized-ecosystem proxy' },
  { id: 'ada',  label: 'ADA',  desc: 'Cardano — large-cap alt, slower-moving than SOL' },
  { id: 'doge', label: 'DOGE', desc: 'Dogecoin — memecoin / retail-sentiment proxy' },
];

const REGIMES = [
  { id: 'strong_uptrend',   label: 'Strong Uptrend',   color: 'emerald', icon: TrendingUp },
  { id: 'uptrend',          label: 'Uptrend',          color: 'emerald', icon: TrendingUp },
  { id: 'range',            label: 'Range',            color: 'blue',    icon: Minus },
  { id: 'volatile',         label: 'Volatile',         color: 'amber',   icon: Activity },
  { id: 'downtrend',        label: 'Downtrend',        color: 'red',     icon: TrendingDown },
  { id: 'strong_downtrend', label: 'Strong Downtrend', color: 'red',     icon: TrendingDown },
];

const MOMENTUM_OPTIONS = [
  { id: '',         label: 'Any' },
  { id: 'rising',   label: 'Rising' },
  { id: 'flat',     label: 'Flat' },
  { id: 'falling',  label: 'Falling' },
];

const INNER_BOT_TYPE_LABELS = {
  dex_grid_bot:   'Grid Bot',
  dex_tv_webhook: 'TradingView Webhook',
  dex_dca:        'DCA',
};

const regimeColorMap = {
  emerald: { bg: 'bg-emerald-500/10', text: 'text-emerald-400', border: 'border-emerald-500/30' },
  blue:    { bg: 'bg-blue-500/10',    text: 'text-blue-300',    border: 'border-blue-500/30'   },
  amber:   { bg: 'bg-amber-500/10',   text: 'text-amber-300',   border: 'border-amber-500/30'  },
  red:     { bg: 'bg-red-500/10',     text: 'text-red-400',     border: 'border-red-500/30'    },
  gray:    { bg: 'bg-dark-600',       text: 'text-gray-400',    border: 'border-dark-500'      },
};

function regimeStyle(id) {
  const r = REGIMES.find(x => x.id === id);
  if (!r) return regimeColorMap.gray;
  return regimeColorMap[r.color];
}

function fmtTime(iso) { try { return new Date(iso).toLocaleString(); } catch { return iso || '—'; } }
function fmtTimeShort(iso) { try { return new Date(iso).toLocaleTimeString(); } catch { return '—'; } }
function fmtPct(n, digits = 2) {
  const v = Number(n);
  if (!Number.isFinite(v)) return '—';
  return `${v > 0 ? '+' : ''}${v.toFixed(digits)}%`;
}

function Toast({ message, type = 'success', onDismiss }) {
  useEffect(() => { const t = setTimeout(onDismiss, 6000); return () => clearTimeout(t); }, [onDismiss]);
  return (
    <div className={`fixed bottom-6 right-6 z-50 px-5 py-3 rounded-xl shadow-2xl text-sm font-medium max-w-md
      ${type === 'success' ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30'
        : type === 'error' ? 'bg-red-500/20 text-red-300 border border-red-500/30'
        : 'bg-amber-500/20 text-amber-300 border border-amber-500/30'}`}>
      {message}
    </div>
  );
}
function Card({ children, className = '' }) {
  return <div className={`bg-dark-800 rounded-2xl p-6 border border-dark-700/50 ${className}`}>{children}</div>;
}

// ─── Regime badge ───────────────────────────────────────────────────────────
function RegimeBadge({ regime, size = 'sm' }) {
  const r = REGIMES.find(x => x.id === regime);
  const style = regimeStyle(regime);
  const Icon = r?.icon || Minus;
  const sizing = size === 'lg'
    ? 'px-3 py-1.5 text-sm'
    : 'px-2 py-0.5 text-[11px]';
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full font-medium border ${style.bg} ${style.text} ${style.border} ${sizing}`}>
      <Icon className="w-3 h-3" />
      {r?.label || regime || 'unknown'}
    </span>
  );
}

// ─── Inner bot picker — used both in rule editor + fallback selector ────────
function InnerBotPicker({ value, onChange, innerBots, allowNone = false, allowPauseAll = false }) {
  const all = useMemo(() => {
    const out = [];
    for (const b of (innerBots?.gridBots   || [])) out.push(b);
    for (const b of (innerBots?.tvWebhooks || [])) out.push(b);
    for (const b of (innerBots?.dcaPlans   || [])) out.push(b);
    return out;
  }, [innerBots]);

  const selected = value && value.type && value.id ? `${value.type}:${value.id}` : '';

  function setFromKey(key) {
    if (!key)              return onChange(null);
    if (key === '__pause') return onChange({ pauseAll: true });
    const [type, id] = key.split(':', 2);
    onChange({ type, id });
  }

  return (
    <select value={value?.pauseAll ? '__pause' : selected}
      onChange={e => setFromKey(e.target.value)}
      className="w-full bg-dark-700 border border-dark-600 rounded-xl px-3 py-2 text-white text-xs focus:outline-none focus:border-accent-primary">
      {allowNone && <option value="">— none —</option>}
      {allowPauseAll && <option value="__pause">Pause all inner bots</option>}
      {all.length === 0 && (
        <option value="" disabled>No DEX bots yet — create a Grid Bot / TradingView Webhook / DCA first</option>
      )}
      {(innerBots?.gridBots?.length > 0) && (
        <optgroup label="Grid Bots">
          {innerBots.gridBots.map(b => (
            <option key={b.id} value={`${b.type}:${b.id}`}
              disabled={!b.usable}>{b.label}{b.status === 'cancelled' || b.status === 'completed' ? ` (${b.status})` : ''}</option>
          ))}
        </optgroup>
      )}
      {(innerBots?.tvWebhooks?.length > 0) && (
        <optgroup label="TradingView Webhooks">
          {innerBots.tvWebhooks.map(b => (
            <option key={b.id} value={`${b.type}:${b.id}`}>{b.label}</option>
          ))}
        </optgroup>
      )}
      {(innerBots?.dcaPlans?.length > 0) && (
        <optgroup label="DCA Plans">
          {innerBots.dcaPlans.map(b => (
            <option key={b.id} value={`${b.type}:${b.id}`}
              disabled={!b.usable}>{b.label}{b.status === 'completed' || b.status === 'cancelled' ? ` (${b.status})` : ''}</option>
          ))}
        </optgroup>
      )}
    </select>
  );
}

// ─── Rule editor ────────────────────────────────────────────────────────────
function RuleEditor({ rule, idx, onChange, onRemove, innerBots, canRemove }) {
  function patch(p) { onChange({ ...rule, ...p }); }
  function patchWhen(w) { onChange({ ...rule, when: { ...rule.when, ...w } }); }

  return (
    <div className="bg-dark-700/30 border border-dark-600 rounded-xl p-3 space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-bold text-accent-primary uppercase tracking-wider">Rule {idx + 1}</span>
          <input value={rule.name || ''}
            onChange={e => patch({ name: e.target.value })}
            placeholder="e.g. Strong uptrend → momentum bot"
            className="bg-transparent border-b border-dark-600 px-1 py-0.5 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-accent-primary flex-1 min-w-0" />
        </div>
        {canRemove && (
          <button onClick={onRemove} title="Remove rule"
            className="p-1 rounded-lg text-red-400 hover:bg-red-500/10">
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {/* When */}
      <div>
        <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold mb-1.5">When</div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-[10px] text-gray-500 block mb-0.5">Regime</label>
            <select value={rule.when?.regime || ''}
              onChange={e => patchWhen({ regime: e.target.value || undefined })}
              className="w-full bg-dark-700 border border-dark-600 rounded-lg px-2 py-1.5 text-xs text-white focus:outline-none focus:border-accent-primary">
              <option value="">Any regime</option>
              {REGIMES.map(r => <option key={r.id} value={r.id}>{r.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] text-gray-500 block mb-0.5">Momentum</label>
            <select value={rule.when?.momentum || ''}
              onChange={e => patchWhen({ momentum: e.target.value || undefined })}
              className="w-full bg-dark-700 border border-dark-600 rounded-lg px-2 py-1.5 text-xs text-white focus:outline-none focus:border-accent-primary">
              {MOMENTUM_OPTIONS.map(m => <option key={m.id} value={m.id}>{m.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] text-gray-500 block mb-0.5">Min trend strength (-1 to 1)</label>
            <input type="number" min="-1" max="1" step="0.1"
              value={rule.when?.minTrend ?? ''}
              onChange={e => patchWhen({ minTrend: e.target.value === '' ? undefined : Number(e.target.value) })}
              placeholder="—"
              className="w-full bg-dark-700 border border-dark-600 rounded-lg px-2 py-1.5 text-xs text-white focus:outline-none focus:border-accent-primary" />
          </div>
          <div>
            <label className="text-[10px] text-gray-500 block mb-0.5">Max trend strength (-1 to 1)</label>
            <input type="number" min="-1" max="1" step="0.1"
              value={rule.when?.maxTrend ?? ''}
              onChange={e => patchWhen({ maxTrend: e.target.value === '' ? undefined : Number(e.target.value) })}
              placeholder="—"
              className="w-full bg-dark-700 border border-dark-600 rounded-lg px-2 py-1.5 text-xs text-white focus:outline-none focus:border-accent-primary" />
          </div>
          <div>
            <label className="text-[10px] text-gray-500 block mb-0.5">Max volatility %</label>
            <input type="number" min="0" step="0.5"
              value={rule.when?.maxVolatilityPct ?? ''}
              onChange={e => patchWhen({ maxVolatilityPct: e.target.value === '' ? undefined : Number(e.target.value) })}
              placeholder="—"
              className="w-full bg-dark-700 border border-dark-600 rounded-lg px-2 py-1.5 text-xs text-white focus:outline-none focus:border-accent-primary" />
          </div>
          <div>
            <label className="text-[10px] text-gray-500 block mb-0.5">RSI below</label>
            <input type="number" min="0" max="100" step="1"
              value={rule.when?.rsiBelow ?? ''}
              onChange={e => patchWhen({ rsiBelow: e.target.value === '' ? undefined : Number(e.target.value) })}
              placeholder="—"
              className="w-full bg-dark-700 border border-dark-600 rounded-lg px-2 py-1.5 text-xs text-white focus:outline-none focus:border-accent-primary" />
          </div>
        </div>
      </div>

      {/* Then */}
      <div>
        <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold mb-1.5">Then activate</div>
        <InnerBotPicker
          value={rule.thenAction === 'pause_all' ? { pauseAll: true } : rule.thenBot}
          onChange={(v) => {
            if (!v)                    patch({ thenBot: null, thenAction: null });
            else if (v.pauseAll)       patch({ thenBot: null, thenAction: 'pause_all' });
            else                       patch({ thenBot: v, thenAction: null });
          }}
          innerBots={innerBots}
          allowNone={false}
          allowPauseAll={true}
        />
      </div>
    </div>
  );
}

// ─── Regime dashboard ───────────────────────────────────────────────────────
function RegimeDashboard({ condBot, onSimulate, simulating }) {
  const rd = condBot?.lastRegimeData;
  const style = regimeStyle(condBot?.lastRegime);
  return (
    <div className={`rounded-xl border ${style.border} ${style.bg} px-4 py-3`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold mb-1">Current regime</div>
          <div className="flex items-center gap-2 flex-wrap">
            <RegimeBadge regime={condBot?.lastRegime || 'unknown'} size="lg" />
            {condBot?.pendingRegime && condBot?.pendingTicks > 0 && (
              <span className="text-[11px] px-2 py-1 rounded-full bg-amber-500/10 text-amber-300 border border-amber-500/30">
                pending → {condBot.pendingRegime} ({condBot.pendingTicks}/3)
              </span>
            )}
          </div>
          {rd?.dataSufficient && (
            <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
              <div>
                <div className="text-[10px] text-gray-500 uppercase tracking-wider">Close</div>
                <div className="text-white font-mono">${rd.close?.toFixed(2)}</div>
              </div>
              <div>
                <div className="text-[10px] text-gray-500 uppercase tracking-wider">Trend</div>
                <div className={`font-mono ${rd.trendStrength >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                  {rd.trendStrength > 0 ? '+' : ''}{rd.trendStrength.toFixed(2)}
                </div>
              </div>
              <div>
                <div className="text-[10px] text-gray-500 uppercase tracking-wider">Volatility</div>
                <div className="text-white font-mono">{rd.volatilityPct?.toFixed(2)}%</div>
              </div>
              <div>
                <div className="text-[10px] text-gray-500 uppercase tracking-wider">RSI · Mo</div>
                <div className="text-white font-mono">{rd.rsi?.toFixed(0) ?? '—'} · {rd.momentum}</div>
              </div>
            </div>
          )}
          {!rd?.dataSufficient && (
            <p className="text-[11px] text-gray-400 mt-1">Worker hasn't taken first tick yet, or candles incomplete.</p>
          )}
        </div>
        <button onClick={onSimulate} disabled={simulating}
          title="Classify current regime without acting"
          className="px-3 py-1.5 rounded-lg bg-dark-700 text-accent-primary hover:bg-accent-primary/10 border border-dark-600 text-xs font-semibold flex items-center gap-1.5 disabled:opacity-50">
          {simulating ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <ZoomIn className="w-3.5 h-3.5" />}
          Test now
        </button>
      </div>
    </div>
  );
}

// ─── Conditional bot row (expandable) ───────────────────────────────────────
function CondBotRow({ bot, onActivate, onDeactivate, onDelete, onEdit, busy }) {
  const [expanded, setExpanded] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [simulating, setSimulating] = useState(false);
  const [simulation, setSimulation] = useState(null);
  const refAsset = REFERENCE_ASSETS.find(r => r.id === bot.referenceAssetId);
  const activeRule = bot.rules?.find(r => r.id === bot.activeRuleId);

  async function loadHistory() {
    setHistoryLoading(true);
    try {
      const r = await getDexConditionalBot(bot.condBotId);
      setHistory(r.data?.history || []);
    } catch {} finally { setHistoryLoading(false); }
  }
  useEffect(() => { if (expanded) loadHistory(); }, [expanded, bot.condBotId]);

  async function handleSimulate() {
    setSimulating(true);
    try {
      const r = await simulateDexConditionalBot(bot.condBotId);
      setSimulation(r.data);
    } catch (err) {
      setSimulation({ error: err?.message || 'Simulation failed' });
    } finally { setSimulating(false); }
  }

  return (
    <div className="bg-dark-700/40 rounded-xl border border-dark-600/40 overflow-hidden hover:border-dark-500/60 transition-colors">
      <button onClick={() => setExpanded(e => !e)}
        className="w-full px-4 py-3 text-left transition-colors hover:bg-dark-600/20">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <GitBranch className="w-3.5 h-3.5 text-accent-primary" />
              <span className="text-white text-sm font-semibold">{bot.name}</span>
              <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium border ${
                bot.status === 'active' ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30 animate-[cb-pulse_2s_ease-in-out_infinite]'
                : 'bg-amber-500/10 text-amber-300 border-amber-500/30'
              }`}>{bot.status === 'active' ? 'Running' : 'Paused'}</span>
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-dark-600 text-gray-400 border border-dark-500">
                {refAsset?.label || bot.referenceAssetId} regime
              </span>
              {bot.lastRegime && <RegimeBadge regime={bot.lastRegime} />}
            </div>
            <p className="text-gray-400 text-xs mt-1">
              {bot.rules?.length || 0} rule{bot.rules?.length === 1 ? '' : 's'}
              {bot.activeBot && (
                <> · Active: <span className="text-emerald-400">{INNER_BOT_TYPE_LABELS[bot.activeBot.type]} {String(bot.activeBot.id).slice(0, 8)}…</span></>
              )}
              {bot.lastTickAt && (
                <> · Last tick: <span className="text-gray-300">{fmtTimeShort(bot.lastTickAt)}</span></>
              )}
              {bot.ticksRun > 0 && (
                <> · {bot.ticksRun} ticks · {bot.switchCount || 0} switches</>
              )}
            </p>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0" onClick={e => e.stopPropagation()}>
            {bot.status === 'active' ? (
              <button onClick={() => onDeactivate(bot.condBotId)} disabled={busy} title="Pause orchestrator (inner bots stay in current state)"
                className="p-1.5 rounded-lg bg-dark-700 text-amber-400 hover:bg-amber-500/10 disabled:opacity-50 transition-colors">
                <PauseCircle className="w-3.5 h-3.5" />
              </button>
            ) : (
              <button onClick={() => onActivate(bot.condBotId)} disabled={busy} title="Activate orchestrator"
                className="p-1.5 rounded-lg bg-dark-700 text-emerald-400 hover:bg-emerald-500/10 disabled:opacity-50 transition-colors">
                <PlayCircle className="w-3.5 h-3.5" />
              </button>
            )}
            <button onClick={() => onEdit(bot)} disabled={busy} title="Edit"
              className="p-1.5 rounded-lg bg-dark-700 text-gray-400 hover:text-white hover:bg-dark-600 disabled:opacity-50 transition-colors">
              <Edit2 className="w-3.5 h-3.5" />
            </button>
            <button onClick={() => onDelete(bot.condBotId)} disabled={busy} title="Delete"
              className="p-1.5 rounded-lg bg-dark-700 text-red-400 hover:bg-red-500/10 disabled:opacity-50 transition-colors">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
            <span className="ml-1 text-gray-500">
              {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </span>
          </div>
        </div>
      </button>

      {expanded && (
        <div className="px-4 py-3 border-t border-dark-600/40 bg-dark-800/40 space-y-3 animate-[cb-fade_0.2s_ease-out]">
          {/* Regime dashboard */}
          <RegimeDashboard condBot={bot} onSimulate={handleSimulate} simulating={simulating} />

          {/* Simulation result */}
          {simulation && (
            <div className="bg-accent-primary/5 border border-accent-primary/20 rounded-lg px-3 py-2">
              <div className="text-[10px] uppercase tracking-wider text-accent-primary font-semibold mb-1">Test classification</div>
              {simulation.error ? (
                <p className="text-red-400 text-xs">{simulation.error}</p>
              ) : (
                <div className="text-xs space-y-1">
                  <div>Regime: <RegimeBadge regime={simulation.regimeData?.regime} /></div>
                  <div className="text-gray-400">Trend: <span className="text-white font-mono">{simulation.regimeData?.trendStrength?.toFixed(2)}</span> · Volatility: <span className="text-white font-mono">{simulation.regimeData?.volatilityPct?.toFixed(2)}%</span> · RSI: <span className="text-white font-mono">{simulation.regimeData?.rsi?.toFixed(0) ?? '—'}</span></div>
                  <div className="text-gray-400">Would activate: <span className="text-emerald-400 font-semibold">{simulation.pickedRule?.name}</span>{simulation.isFallback && <span className="text-amber-300"> (fallback)</span>}</div>
                  <div className="text-[10px] text-gray-500">Pulled {simulation.candleCount} candles from {simulation.referenceAsset?.exchange} · {simulation.referenceAsset?.symbol}</div>
                </div>
              )}
            </div>
          )}

          {/* Rules list */}
          <div>
            <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold mb-1.5">Rules ({bot.rules?.length || 0})</div>
            <div className="space-y-1.5">
              {bot.rules?.map((r, i) => {
                const isActive = bot.activeRuleId === r.id;
                return (
                  <div key={r.id || i}
                    className={`px-3 py-2 rounded-lg border text-xs ${
                      isActive
                        ? 'bg-emerald-500/5 border-emerald-500/30 text-white'
                        : 'bg-dark-700/40 border-dark-600 text-gray-300'}`}>
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <span className="font-semibold flex items-center gap-1.5">
                        {isActive && <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />}
                        {r.name}
                      </span>
                      <span className="text-[10px] text-gray-500">#{i + 1}</span>
                    </div>
                    <div className="text-[11px] text-gray-400">
                      When: {r.when?.regime || 'any regime'}
                      {r.when?.minTrend != null && ` · trend≥${r.when.minTrend}`}
                      {r.when?.maxTrend != null && ` · trend≤${r.when.maxTrend}`}
                      {r.when?.maxVolatilityPct != null && ` · vol≤${r.when.maxVolatilityPct}%`}
                      {r.when?.rsiBelow != null && ` · RSI<${r.when.rsiBelow}`}
                      {r.when?.rsiAbove != null && ` · RSI>${r.when.rsiAbove}`}
                      {r.when?.momentum && ` · momentum=${r.when.momentum}`}
                    </div>
                    <div className="text-[11px] text-gray-400 mt-0.5">
                      Then: {r.thenBot
                        ? <>activate <span className="text-emerald-400">{INNER_BOT_TYPE_LABELS[r.thenBot.type]} {String(r.thenBot.id).slice(0, 8)}…</span></>
                        : <span className="text-amber-300">pause all inner bots</span>}
                    </div>
                  </div>
                );
              })}
            </div>
            {bot.fallbackBot && (
              <div className="px-3 py-2 mt-1.5 rounded-lg border border-dashed border-dark-500 text-xs text-gray-400">
                Fallback: activate <span className="text-blue-300">{INNER_BOT_TYPE_LABELS[bot.fallbackBot.type]} {String(bot.fallbackBot.id).slice(0, 8)}…</span>
              </div>
            )}
          </div>

          {/* History */}
          <div>
            <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold mb-1.5">Recent activity</div>
            {historyLoading ? (
              <div className="flex justify-center py-4"><Spinner size="sm" /></div>
            ) : history.length === 0 ? (
              <div className="bg-dark-700/30 border border-dashed border-dark-600 rounded-lg px-3 py-4 text-center">
                <p className="text-gray-500 text-xs">No events yet. Activate the bot to start polling.</p>
              </div>
            ) : (
              <div className="space-y-1 max-h-64 overflow-y-auto">
                {history.map((h, i) => {
                  const kindCls = h.kind === 'switch' ? 'text-accent-primary'
                                : h.kind === 'regime_change' ? 'text-blue-300'
                                : h.kind === 'activate' ? 'text-emerald-400'
                                : h.kind === 'deactivate' ? 'text-amber-300'
                                : 'text-gray-400';
                  return (
                    <div key={i} className="flex items-center justify-between text-[11px] px-3 py-1.5 bg-dark-700/40 rounded-lg">
                      <span className="flex items-center gap-2">
                        <span className={`font-semibold uppercase tracking-wider text-[10px] ${kindCls}`}>{h.kind}</span>
                        <span className="text-gray-300">
                          {h.kind === 'switch' && <>→ {h.ruleName} {h.target && `· activate ${INNER_BOT_TYPE_LABELS[h.target.type]}`}</>}
                          {h.kind === 'regime_change' && <>{h.toRegime} <span className="text-gray-500">(trend {h.trendStrength?.toFixed(2)}, vol {h.volatilityPct?.toFixed(1)}%)</span></>}
                          {h.kind === 'activate' && <>user activated</>}
                          {h.kind === 'deactivate' && <>user deactivated</>}
                        </span>
                      </span>
                      <span className="text-gray-500 text-[10px]">{fmtTimeShort(h.recordedAt)}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Create / Edit panel ────────────────────────────────────────────────────
function CreateCondBotPanel({ onSaved, onToast, innerBots, editingBot, onCancelEdit }) {
  const isEdit = !!editingBot;
  const [name,         setName]         = useState(editingBot?.name || '');
  const [description,  setDescription]  = useState(editingBot?.description || '');
  const [referenceAssetId, setReferenceAssetId] = useState(editingBot?.referenceAssetId || 'btc');
  const [rules,        setRules]        = useState(editingBot?.rules?.length ? editingBot.rules : [{
    name: 'Range → grid', when: { regime: 'range' }, thenBot: null, thenAction: null,
  }]);
  const [fallbackBot,  setFallbackBot]  = useState(editingBot?.fallbackBot || null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (editingBot) {
      setName(editingBot.name || '');
      setDescription(editingBot.description || '');
      setReferenceAssetId(editingBot.referenceAssetId || 'btc');
      setRules(editingBot.rules?.length ? editingBot.rules : [{ name: 'Range → grid', when: { regime: 'range' }, thenBot: null, thenAction: null }]);
      setFallbackBot(editingBot.fallbackBot || null);
    }
  }, [editingBot]);

  function addRule() {
    setRules([...rules, { name: `Rule ${rules.length + 1}`, when: {}, thenBot: null, thenAction: null }]);
  }
  function updateRule(idx, next) {
    const out = [...rules]; out[idx] = next; setRules(out);
  }
  function removeRule(idx) {
    setRules(rules.filter((_, i) => i !== idx));
  }

  async function handleSave() {
    if (!name.trim()) { onToast({ type: 'error', message: 'Give the bot a name' }); return; }
    if (rules.length === 0) { onToast({ type: 'error', message: 'Add at least one rule' }); return; }
    setSaving(true);
    try {
      if (isEdit) {
        await updateDexConditionalBot(editingBot.condBotId, {
          name, description, referenceAssetId, rules, fallbackBot,
        });
        onToast({ type: 'success', message: 'Conditional bot updated.' });
      } else {
        await createDexConditionalBot({
          name, description, referenceAssetId, rules, fallbackBot,
        });
        onToast({ type: 'success', message: 'Conditional bot saved — activate it to start orchestrating.' });
        setName(''); setDescription('');
        setRules([{ name: 'Range → grid', when: { regime: 'range' }, thenBot: null, thenAction: null }]);
        setFallbackBot(null);
      }
      onSaved();
    } catch (err) {
      onToast({ type: 'error', message: err?.message || 'Save failed' });
    } finally { setSaving(false); }
  }

  return (
    <Card>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-accent-primary/20 to-accent-primary/5 text-accent-primary flex items-center justify-center border border-accent-primary/20">
            <GitBranch className="w-4 h-4" />
          </div>
          <div>
            <h3 className="text-white font-semibold text-sm">{isEdit ? `Edit "${editingBot.name}"` : 'New Conditional Bot'}</h3>
            <p className="text-gray-500 text-xs">{isEdit ? 'Modify rules + inner bot wiring' : 'Auto-switch which DEX bot runs based on market regime'}</p>
          </div>
        </div>
        {isEdit && (
          <button onClick={onCancelEdit} title="Cancel edit"
            className="p-1.5 rounded-lg text-gray-400 hover:text-white hover:bg-dark-700">
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      <div className="space-y-4">
        {/* Name + description */}
        <div>
          <label className="text-gray-500 text-xs uppercase tracking-wide mb-1.5 block">Name</label>
          <input value={name} onChange={e => setName(e.target.value)}
            maxLength={80} placeholder="e.g. BTC-regime ETH accumulator"
            className="w-full bg-dark-700 border border-dark-600 rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-accent-primary placeholder-gray-600" />
        </div>

        {/* Reference asset */}
        <div>
          <label className="text-gray-500 text-xs uppercase tracking-wide mb-1.5 flex items-center gap-1">
            Reference asset (for regime classification)
            <InfoTip side="right">10 assets supported. BTC drives most of crypto — use it unless your inner bots are pair-specific. Doesn't have to match your inner bots' pairs.</InfoTip>
          </label>
          <div className="grid grid-cols-3 gap-2">
            {REFERENCE_ASSETS.map(r => (
              <button key={r.id} type="button" onClick={() => setReferenceAssetId(r.id)}
                title={r.desc}
                className={`px-3 py-2 rounded-xl text-xs font-semibold border transition-all ${
                  referenceAssetId === r.id ? 'bg-accent-primary/10 border-accent-primary/40 text-white' : 'bg-dark-700 border-dark-600 text-gray-400 hover:bg-dark-600'}`}>
                {r.label}
              </button>
            ))}
          </div>
          <p className="text-[10px] text-gray-500 mt-1">Reads OHLCV from Binance for regime detection. The reference doesn't have to match your inner bots' pairs — BTC regime is often used to gate altcoin strategies.</p>
        </div>

        {/* Rules */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-gray-500 text-xs uppercase tracking-wide flex items-center gap-1">
              Rules ({rules.length}/10)
              <InfoTip side="right">Each rule = WHEN (regime + momentum + min trend) → THEN (activate one bot OR Pause all). Rules evaluate top-down, first match wins. Order specific rules above general ones.</InfoTip>
            </span>
            <button onClick={addRule} disabled={rules.length >= 10}
              className="text-[11px] px-2 py-0.5 rounded bg-accent-primary/10 text-accent-primary hover:bg-accent-primary/20 disabled:opacity-50 border border-accent-primary/20 flex items-center gap-1">
              <Plus className="w-3 h-3" /> Add rule
            </button>
          </div>
          <div className="space-y-2">
            {rules.map((r, i) => (
              <RuleEditor key={i} rule={r} idx={i}
                onChange={next => updateRule(i, next)}
                onRemove={() => removeRule(i)}
                innerBots={innerBots}
                canRemove={rules.length > 1} />
            ))}
          </div>
          <p className="text-[10px] text-gray-500 mt-1.5">Rules evaluate top-down. First match wins. If none match and no fallback is set, all orchestrated bots get paused.</p>
        </div>

        {/* Fallback */}
        <div>
          <label className="text-gray-500 text-xs uppercase tracking-wide mb-1.5 flex items-center gap-1">
            Fallback bot (optional)
            <InfoTip side="right">Runs when NO rule matches the current regime. Leave blank to pause all in that case. Use it for a default "always-on" inner bot like DCA.</InfoTip>
          </label>
          <InnerBotPicker
            value={fallbackBot}
            onChange={setFallbackBot}
            innerBots={innerBots}
            allowNone={true}
            allowPauseAll={false}
          />
          <p className="text-[10px] text-gray-500 mt-1">Activated when no rule matches. Leave blank to pause all when no rule matches.</p>
        </div>

        <button onClick={handleSave} disabled={saving}
          className="w-full py-3 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 bg-gradient-to-r from-accent-primary to-amber-500 text-dark-900 shadow-lg shadow-accent-primary/20 hover:shadow-xl hover:shadow-accent-primary/30 hover:-translate-y-px transition-all disabled:opacity-50 disabled:cursor-not-allowed">
          {saving ? <Spinner size="sm" /> : (isEdit ? <Save className="w-4 h-4" /> : <Sparkles className="w-4 h-4" />)}
          {saving ? 'Saving…' : (isEdit ? 'Save changes' : 'Save conditional bot')}
        </button>
        <p className="text-gray-500 text-[11px] text-center">
          After saving you can activate the orchestrator. The worker classifies regime every minute; switches require 3 consecutive ticks of agreement (hysteresis) to commit.
        </p>
      </div>
    </Card>
  );
}

// ─── Page ───────────────────────────────────────────────────────────────────
export default function DexConditionalBotPage() {
  const [bots, setBots] = useState([]);
  const [innerBots, setInnerBots] = useState({ gridBots: [], tvWebhooks: [], dcaPlans: [] });
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [busy, setBusy] = useState(null);
  const [editing, setEditing] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [list, inner] = await Promise.all([
        listDexConditionalBots(),
        listDexConditionalBotInnerBots(),
      ]);
      setBots(list.data?.condBots || []);
      setInnerBots(inner.data || { gridBots: [], tvWebhooks: [], dcaPlans: [] });
    } catch (err) {
      setToast({ type: 'error', message: err?.message || 'Could not load conditional bots' });
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { reload(); }, [reload]);

  const handleActivate = async (id) => {
    setBusy(id);
    try { await activateDexConditionalBot(id); await reload(); setToast({ type: 'success', message: 'Conditional bot activated. Worker starts polling next tick (~1 min).' }); }
    catch (err) { setToast({ type: 'error', message: err?.message || 'Activate failed' }); }
    finally { setBusy(null); }
  };
  const handleDeactivate = async (id) => {
    setBusy(id);
    try { await deactivateDexConditionalBot(id); await reload(); setToast({ type: 'success', message: 'Paused. Inner bots stay in their current state (you can manage them individually).' }); }
    catch (err) { setToast({ type: 'error', message: err?.message || 'Deactivate failed' }); }
    finally { setBusy(null); }
  };
  const handleDelete = async (id) => {
    const bot = bots.find(b => b.condBotId === id);
    if (bot?.status === 'active') { setToast({ type: 'error', message: 'Deactivate the bot first, then delete.' }); return; }
    if (!window.confirm('Delete this conditional bot? Inner bots will be left in their current state.')) return;
    setBusy(id);
    try { await deleteDexConditionalBot(id); await reload(); setToast({ type: 'success', message: 'Deleted.' }); }
    catch (err) { setToast({ type: 'error', message: err?.message || 'Delete failed' }); }
    finally { setBusy(null); }
  };

  const active   = bots.filter(b => b.status === 'active');
  const inactive = bots.filter(b => b.status !== 'active');

  return (
    <div className="space-y-6">
      <style>{`
        @keyframes cb-pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.65; } }
        @keyframes cb-fade { from { opacity: 0; transform: translateY(-4px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>
      <div>
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-accent-primary/25 to-accent-primary/5 text-accent-primary flex items-center justify-center border border-accent-primary/20">
            <GitBranch className="w-5 h-5" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">DEX Conditional Bot</h1>
            <p className="text-gray-400 text-sm">Regime-switching orchestrator. Auto-activates the right DEX bot for the current market regime — Grid Bot in ranges, DCA in downtrends, TradingView Webhooks for signals. Hysteresis built-in.</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <CreateCondBotPanel
          onSaved={() => { setEditing(null); reload(); }}
          onToast={setToast}
          innerBots={innerBots}
          editingBot={editing}
          onCancelEdit={() => setEditing(null)} />

        <Card>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-white font-semibold text-sm">Active conditional bots</h3>
              <p className="text-gray-500 text-xs">{active.length} orchestrating now</p>
            </div>
            <button onClick={reload} disabled={loading}
              className="btn-ghost text-xs px-3 py-1.5 flex items-center gap-1.5">
              <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} /> Reload
            </button>
          </div>
          {loading ? (
            <div className="flex justify-center py-10"><Spinner /></div>
          ) : active.length === 0 ? (
            <div className="text-center py-10">
              <Brain className="w-8 h-8 text-gray-600 mx-auto mb-2" />
              <p className="text-gray-500 text-sm">No active conditional bots.</p>
              <p className="text-gray-600 text-[11px] mt-1">Build rules → activate → the worker handles the rest.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {active.map(b => (
                <CondBotRow key={b.condBotId} bot={b}
                  onActivate={handleActivate} onDeactivate={handleDeactivate}
                  onDelete={handleDelete} onEdit={setEditing}
                  busy={busy === b.condBotId} />
              ))}
            </div>
          )}
        </Card>
      </div>

      {inactive.length > 0 && (
        <Card>
          <h3 className="text-white font-semibold text-sm mb-3">Inactive ({inactive.length})</h3>
          <div className="space-y-2">
            {inactive.map(b => (
              <CondBotRow key={b.condBotId} bot={b}
                onActivate={handleActivate} onDeactivate={handleDeactivate}
                onDelete={handleDelete} onEdit={setEditing}
                busy={busy === b.condBotId} />
            ))}
          </div>
        </Card>
      )}

      {toast && <Toast {...toast} onDismiss={() => setToast(null)} />}
    </div>
  );
}
